// ── WAV Recording Helper ──────────────────────────────────
// Records directly to WAV using ScriptProcessor — no webm/ffmpeg needed

export class WavRecorder {
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;
  private chunks: Float32Array[] = [];
  private sampleRate = 48000;
  private recording = false;
  onChunk: ((chunk: Float32Array) => void) | null = null;

  async init() {
    // Phase 1: get mic permission + set up audio graph.
    // Audio flows but we don't capture yet (countdown phase).
    this.ctx = new AudioContext({ sampleRate: this.sampleRate });
    this.sampleRate = this.ctx.sampleRate;
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.processor = this.ctx.createScriptProcessor(1024, 1, 1);
    this.chunks = [];
    this.recording = false;

    this.processor.onaudioprocess = (e) => {
      if (!this.recording) return; // discard during countdown
      const data = e.inputBuffer.getChannelData(0);
      const copy = new Float32Array(data);
      this.chunks.push(copy);
      if (this.onChunk) this.onChunk(copy);
    };

    this.processor.connect(this.ctx.destination);
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.source.connect(this.processor);
    // Mic is now hot — audio flowing, but not captured until startCapture()
  }

  startCapture() {
    // Phase 2: actually start recording chunks (after countdown)
    this.chunks = [];
    this.recording = true;
  }

  async stop(): Promise<Blob> {
    // Wait 250ms to capture remaining buffers
    await new Promise(r => setTimeout(r, 250));
    this.recording = false;
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach(t => t.stop());
    this.ctx?.close();

    // Merge chunks into single buffer
    const totalLen = this.chunks.reduce((s, c) => s + c.length, 0);
    const merged = new Float32Array(totalLen);
    let offset = 0;
    for (const chunk of this.chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    // Trim leading silence only (mic/AudioContext startup latency).
    // Don't trim trailing — the user controls when recording stops.
    const trimThreshold = 0.005;
    let trimStart = 0;
    for (let i = 0; i < merged.length; i++) {
      if (Math.abs(merged[i]) > trimThreshold) { trimStart = Math.max(0, i - 512); break; }
    }
    const trimmed = trimStart > 0 ? merged.slice(trimStart) : merged;

    // Encode as WAV
    return this.encodeWav(trimmed, this.sampleRate);
  }

  private encodeWav(samples: Float32Array, sr: number): Blob {
    const buf = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buf);
    const writeStr = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };

    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sr, true);
    view.setUint32(28, sr * 2, true); // byte rate
    view.setUint16(32, 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample
    writeStr(36, 'data');
    view.setUint32(40, samples.length * 2, true);

    // Convert float32 to int16
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }

    return new Blob([buf], { type: 'audio/wav' });
  }
}
